import { supabase } from '@/integrations/supabase/client';
import { sendPhotoLike } from '@/utils/photoLikes';

export interface FavouriteItem {
  photoId: number;
  participantId: string;
  swissEquestrianRiderId?: string;
  eventId: string;
  raceId: string;
  eventName: string;
  raceCategory: string;
  riderName: string;
  horseName: string;
  photoUrl: string;
  likedAt: string;
}

const STORAGE_KEY = 'equiphoto_favourites';

export const getFavourites = (): FavouriteItem[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveFavourites = (items: FavouriteItem[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('favouritesUpdated'));
};

export const addFavourite = (item: Omit<FavouriteItem, 'likedAt'>) => {
  const favourites = getFavourites();
  const exists = favourites.some(f => f.photoId === item.photoId && f.eventId === item.eventId);
  if (exists) return;
  favourites.push({ ...item, likedAt: new Date().toISOString() });
  saveFavourites(favourites);
};

export const removeFavourite = (photoId: number, eventId: string) => {
  const favourites = getFavourites().filter(f => !(f.photoId === photoId && f.eventId === eventId));
  saveFavourites(favourites);
};

export const isFavourite = (photoId: number, eventId: string): boolean => {
  return getFavourites().some(f => f.photoId === photoId && f.eventId === eventId);
};

export const toggleFavourite = (item: Omit<FavouriteItem, 'likedAt'>): boolean => {
  const liked = !isFavourite(item.photoId, item.eventId);
  if (liked) addFavourite(item); else removeFavourite(item.photoId, item.eventId);
  sendPhotoLike(item.eventId, item.photoId, liked);
  return liked;
};

export const getFavouriteCount = (): number => {
  return getFavourites().length;
};

export const syncFavouritesToDb = async (userId: string) => {
  const localFavs = getFavourites();
  if (localFavs.length === 0) return;

  for (const fav of localFavs) {
    await supabase.from('user_favourites').upsert({
      user_id: userId,
      photo_id: fav.photoId,
      participant_id: fav.participantId || null,
      swiss_equestrian_rider_id: fav.swissEquestrianRiderId || fav.participantId || null,
      event_id: fav.eventId,
      race_id: fav.raceId || null,
      event_name: fav.eventName || null,
      race_category: fav.raceCategory || null,
      rider_name: fav.riderName || null,
      horse_name: fav.horseName || null,
      photo_url: fav.photoUrl || null,
    }, { onConflict: 'user_id,photo_id,event_id' });
  }
};

export const loadFavouritesFromDb = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_favourites')
    .select('photo_id, participant_id, swiss_equestrian_rider_id, event_id, race_id, event_name, race_category, rider_name, horse_name, photo_url, created_at')
    .eq('user_id', userId);

  if (error || !data) return;

  const localFavs = getFavourites();
  const dbFavs: FavouriteItem[] = data.map((d: any) => ({
    photoId: d.photo_id,
    participantId: d.participant_id || '',
    swissEquestrianRiderId: d.swiss_equestrian_rider_id || d.participant_id || '',
    eventId: d.event_id,
    raceId: d.race_id || '',
    eventName: d.event_name || '',
    raceCategory: d.race_category || '',
    riderName: d.rider_name || '',
    horseName: d.horse_name || '',
    photoUrl: d.photo_url || '',
    likedAt: d.created_at,
  }));

  // Merge: dedup by photoId+eventId
  const merged = [...localFavs];
  for (const dbFav of dbFavs) {
    if (!merged.some(m => m.photoId === dbFav.photoId && m.eventId === dbFav.eventId)) {
      merged.push(dbFav);
    }
  }

  saveFavourites(merged);
  // Push merged back to DB
  await syncFavouritesToDb(userId);
};
